import { useState } from "react";
import { Send, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Shared 3-channel feedback module.
 *
 * Props:
 *  - channel: 'customer' | 'commander' | 'partner'
 *  - title, kicker, helper: copy
 *  - modules: [{ key, label, fields: ['title','rating','media_url','product_slug']? }]
 *  - palette: { bg, ink, accent, panel } — gold-on-X tonal control
 */
export default function FeedbackModule({
    channel,
    title,
    kicker,
    helper,
    modules,
    palette = {
        bg: "var(--ig-cream)",
        ink: "var(--ig-forest)",
        accent: "var(--ig-gold)",
        panel: "rgba(255,255,255,0.6)",
    },
    testIdPrefix = "feedback",
    products = [],
    successTitle = "Message received.",
    successText = "Mahalo — the team will take a look.",
    hideTag = false,
}) {
    const buttonInk = palette.buttonInk || palette.ink;
    const [activeModule, setActiveModule] = useState(modules[0].key);
    const [form, setForm] = useState({
        name: "",
        email: "",
        title: "",
        body: "",
        rating: "",
        media_url: "",
        product_slug: products[0]?.slug || "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const current = modules.find((m) => m.key === activeModule) || modules[0];
    const fields = current.fields || ["body"];

    const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.body.trim()) {
            toast.error("Tell us a little more");
            return;
        }
        if (form.email && !form.email.includes("@")) {
            toast.error("Email malformed");
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                channel,
                module: activeModule,
                name: form.name || undefined,
                email: form.email || undefined,
                title: fields.includes("title") ? form.title : undefined,
                body: form.body,
                rating: fields.includes("rating") ? Number(form.rating) || undefined : undefined,
                media_url: fields.includes("media_url") ? form.media_url || undefined : undefined,
                product_slug: fields.includes("product_slug") ? form.product_slug || undefined : undefined,
            };
            await api.post("/hub/feedback", payload);
            setSubmitted(true);
            toast.success("Message received · mahalo");
        } catch (err) {
            const msg = err?.response?.data?.detail || "Could not send message";
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="relative p-6 md:p-8 rounded-sm border"
            style={{
                background: palette.panel,
                borderColor: `${palette.accent}55`,
                color: palette.ink,
            }}
            data-testid={`${testIdPrefix}-card`}
        >
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
                <div>
                    <p
                        className="text-[0.6rem] tracking-[0.3em] uppercase"
                        style={{ color: palette.accent }}
                    >
                        {kicker}
                    </p>
                    <h3 className="font-serif text-2xl md:text-3xl mt-2">
                        {title}
                    </h3>
                </div>
                {!hideTag && (
                    <span
                        className="text-[0.6rem] tracking-[0.3em] uppercase opacity-70"
                        style={{ color: palette.accent }}
                    >
                        Tagged · {channel}
                    </span>
                )}
            </div>
            {helper && (
                <p className="text-sm mt-3 leading-relaxed" data-testid={`${testIdPrefix}-intro`}>
                    {helper}
                </p>
            )}

            {submitted ? (
                <div
                    className="mt-6 p-6 rounded-sm border text-center"
                    style={{ borderColor: `${palette.accent}66` }}
                    data-testid={`${testIdPrefix}-success`}
                >
                    <ShieldCheck size={28} style={{ color: palette.accent }} className="mx-auto" />
                    <p className="font-serif text-xl mt-3">{successTitle}</p>
                    <p className="text-sm mt-1" data-testid={`${testIdPrefix}-confirmation`}>
                        {successText}
                    </p>
                </div>
            ) : (
                <form
                    onSubmit={onSubmit}
                    className="mt-6 space-y-4"
                    data-testid={`${testIdPrefix}-form`}
                >
                    {/* Module tabs */}
                    <div className="flex flex-wrap gap-2">
                        {modules.map((m) => (
                            <button
                                type="button"
                                key={m.key}
                                onClick={() => setActiveModule(m.key)}
                                className="text-[0.6rem] tracking-[0.25em] uppercase py-1.5 px-3 rounded-full border transition-colors"
                                style={{
                                    background:
                                        activeModule === m.key
                                            ? palette.accent
                                            : "transparent",
                                    color:
                                        activeModule === m.key
                                            ? buttonInk
                                            : "currentColor",
                                    borderColor:
                                        activeModule === m.key
                                            ? palette.accent
                                            : `${palette.accent}55`,
                                }}
                                data-testid={`${testIdPrefix}-tab-${m.key}`}
                            >
                                {m.label}
                            </button>
                        ))}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {fields.includes("name") && (
                            <input
                                type="text"
                                placeholder="Your name"
                                value={form.name}
                                onChange={(e) => upd("name", e.target.value)}
                                className="bg-transparent border-b py-2 outline-none placeholder:opacity-60"
                                style={{ borderColor: `${palette.accent}55`, color: palette.ink }}
                                data-testid={`${testIdPrefix}-name`}
                            />
                        )}
                        {fields.includes("email") && (
                            <input
                                type="email"
                                placeholder="you@email.com"
                                value={form.email}
                                onChange={(e) => upd("email", e.target.value)}
                                className="bg-transparent border-b py-2 outline-none placeholder:opacity-60"
                                style={{ borderColor: `${palette.accent}55`, color: palette.ink }}
                                data-testid={`${testIdPrefix}-email`}
                            />
                        )}
                    </div>

                    {fields.includes("title") && (
                        <input
                            type="text"
                            placeholder="Headline"
                            value={form.title}
                            onChange={(e) => upd("title", e.target.value)}
                            className="w-full bg-transparent border-b py-2 outline-none placeholder:opacity-60"
                            style={{ borderColor: `${palette.accent}55`, color: palette.ink }}
                        />
                    )}

                    {fields.includes("product_slug") && products.length > 0 && (
                        <select
                            value={form.product_slug}
                            onChange={(e) => upd("product_slug", e.target.value)}
                            className="w-full bg-transparent border-b py-2 outline-none"
                            style={{ borderColor: `${palette.accent}55`, color: palette.ink }}
                            data-testid={`${testIdPrefix}-product`}
                        >
                            {products.map((p) => (
                                <option key={p.slug} value={p.slug}>
                                    {p.name}
                                </option>
                            ))}
                        </select>
                    )}

                    {fields.includes("rating") && (
                        <div className="flex items-center gap-2 text-xs">
                            <span className="tracking-[0.2em] uppercase">
                                Stars
                            </span>
                            {[1, 2, 3, 4, 5].map((n) => (
                                <button
                                    key={n}
                                    type="button"
                                    onClick={() => upd("rating", String(n))}
                                    className="text-2xl"
                                    style={{
                                        color:
                                            Number(form.rating) >= n
                                                ? palette.accent
                                                : "rgba(0,0,0,0.2)",
                                    }}
                                    data-testid={`${testIdPrefix}-rating-${n}`}
                                >
                                    ★
                                </button>
                            ))}
                        </div>
                    )}

                    {fields.includes("media_url") && (
                        <input
                            type="url"
                            placeholder="Video URL (TikTok / YouTube / Instagram reel)"
                            value={form.media_url}
                            onChange={(e) => upd("media_url", e.target.value)}
                            className="w-full bg-transparent border-b py-2 outline-none placeholder:opacity-60"
                            style={{ borderColor: `${palette.accent}55`, color: palette.ink }}
                            data-testid={`${testIdPrefix}-media`}
                        />
                    )}

                    <textarea
                        rows={4}
                        placeholder={current.placeholder || "Your message…"}
                        value={form.body}
                        onChange={(e) => upd("body", e.target.value)}
                        className="w-full bg-transparent border rounded-sm p-3 outline-none placeholder:opacity-60"
                        style={{ borderColor: `${palette.accent}55`, color: palette.ink }}
                        data-testid={`${testIdPrefix}-body`}
                    />

                    <div className="flex justify-end">
                        <button
                            type="submit"
                            disabled={submitting}
                            className="btn-pill"
                            style={{
                                background: palette.accent,
                                color: buttonInk,
                                border: `1px solid ${palette.accent}`,
                            }}
                            data-testid={`${testIdPrefix}-submit`}
                        >
                            {submitting ? (
                                "Sending…"
                            ) : (
                                <>
                                    Send Message <Send size={13} />
                                </>
                            )}
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
}
